import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'kisiye-ozel-hikaye-kitabi-nasil-yapilir',
	title: 'Çocuğunuzu Kahraman Yaparak Kişiye Özel Bir Hikâye Kitabı Nasıl Yapılır',
	description:
		'Kişiye özel bir hikâye kitabı yapmak için adım adım rehber: fotoğraf seçmek, sanat stili belirlemek, benzerliği doğru yakalamak ve baskıyı onaylamadan önce nelere bakmak gerektiği.',
	date: '2026-02-03',
	heroImage: '/assets/hero/kid-3/watercolor_scene.png',
	tags: ['nasıl yapılır', 'kişiye özel kitaplar'],
	lang: 'tr',
	dateLocale: 'tr-TR',
	body: `Kişiye özel bir hikâye kitabı yapmak çoğunlukla basittir. Yanlış giden kısımlar tahmin edilebilir ve neredeyse hepsi fotoğrafa kadar uzanır.

İşte sürecin tamamı ve her aşamada nelere dikkat etmeniz gerektiği.

## 1. Önce hikâyeyi seçin, sanat stilini değil

Hikâye, tonu belirler. Gece macerası ile deniz altı kurtarma hikâyesi farklı çocuklara uyar ve sanat stili, tam tersi değil, hikâyeyi takip etmelidir.

Dört yaşından küçük bir çocuk için alıyorsanız, daha kısa hikâyeleri ve daha basit sahneleri tercih edin. Yaklaşık on iki resimli sayfa ideal noktadır — bir başlangıcı ve sonu olacak kadar uzun, pazarlık yapılmadan yatmadan önce okunacak kadar kısa.

## 2. Fotoğrafı dikkatli seçin

Bundan sonraki her şeyi belirleyen adım budur. İyi bir fotoğraf, hiç şüphesiz çocuğunuza benzeyen bir karakter üretir; kötü bir fotoğraf ise bir kuzenine benzeyen bir karakter üretir.

**İşe yarayanlar:**

- Cepheden çekilmiş, ya da ona yakın
- Eşit, yumuşak bir ışık — pencere kenarı idealdir
- Yüzün tamamı görünür ve net
- Doğal ya da hafif gülümseyen bir ifade

**Sorun yaratanlar:**

- Şapkalar, güneş gözlükleri ya da yüzü kapatan saçlar
- Güçlü gölgeler, özellikle tepeden vuran güneşten kaynaklananlar
- Aşırı açılar — dik açıyla yukarı ya da aşağı bakmak
- Ağır filtreler, güzellik yumuşatma efektleri ya da agresif gürültü azaltma uygulanmış az ışıklı telefon fotoğrafları
- Çocuğun yüzünün kareye göre küçük kaldığı grup fotoğrafları

Profesyonel bir fotoğrafa ihtiyacınız yok. Kapalı havada iç mekânda çekilmiş bir telefon fotoğrafı, genellikle parlak bir dış mekân fotoğrafından daha iyidir, çünkü ışık daha düzdür.

## 3. Bir sanat stili seçin

Her stil, benzerliğin nasıl okunduğunu değiştirir:

- **Suluboya** — yumuşak, geleneksel, hatıra eşyası havasında. Güzel, ama benzerlik tam değil, izlenimci bir nitelik taşır.
- **Klasik hikâye kitabı** — sıcak ve detaylı, çoğu yetişkinin hatırladığı resimli kitaplara en yakın stil.
- **Düz modern** — cesur şekiller ve güçlü renkler. Çok küçük çocuklar için en iyi sonucu verir.
- **Çizgi roman** — panelli ve enerjik. Büyük çocuklara uyar.
- **Pixar tarzı 3D** — parlak ve render edilmiş. Genellikle ilk bakışta en tanınabilir benzerliği verir ve çocuklara seçim hakkı verildiğinde genellikle bunu seçerler.

Kitap öncelikle çocuk için bir hediyeyse, tanınabilirliğe göre seçin. Öncelikle sizin için bir hatıra eşyasıysa, suluboya ve klasik stiller zamanla daha zarif yaşlanır.

## 4. Kitap oluşturulmadan önce karakteri kontrol edin

Karakter, tek tek sahnelerden önce oluşturulur. Bir sorunu yakalamak için en düşük maliyetli an burasıdır.

Karaktere bakın ve tek bir soru sorun: *bir büyükanne ya da büyükbaba bu çocuğu tanır mıydı?*

Cevap hayırsa, devam etmeden önce farklı bir fotoğraf deneyin. Karakteri şimdi düzeltmek, hiçbir zaman doğru olmayan bir benzerlik üzerine kurulmuş on iki sayfayı incelemekten çok daha hızlıdır.

## 5. Her sayfayı inceleyin

Sahneler oluşturulduktan sonra, üstünkörü göz gezdirmek yerine dikkatlice inceleyin.

Kontrol edilmeye değer noktalar:

- **Tutarlılık.** Karakter her sahnede aynı çocuğa mı benziyor? Çocuklar tutarsızlığı yetişkinlerden çok daha hızlı fark eder ve bu, illüzyonu anında bozar.
- **Eller ve küçük detaylar.** Yapay zekâ ile oluşturulan illüstrasyonların geleneksel olarak en çok zorlandığı yerler burasıdır.
- **Metin yerleşimi.** İllüstrasyondaki önemli hiçbir şeyin kapanmadığından emin olun.
- **Kapak.** En çok bakılan sayfa burasıdır.

Doğru olmayan herhangi bir sayfa yeniden oluşturulabilir. Bunu onaylamadan önce yapın — siz onay vermeden hiçbir şey basılmaz, bu adımın tüm amacı da budur.

## 6. Onaylayın ve bastırın

Onaydan sonra kitap baskıya girer ve kargoya verilir. Zaman payı bırakın: siparişten teslime kadar iki ila üç hafta rahat bir planlama varsayımıdır, Aralık ayında bu süre daha da uzar.

## Diller hakkında bir not

Kitabı Arapça gibi sağdan sola yazılan bir dilde üretiyorsanız, düzenin gerçek anlamda aynalanması gerekir — sayfa sırası, metin hizalaması ve illüstrasyon yerleşimi — sadece soldan sağa bir şablona çevrilmesi yeterli değildir. Soldan sağa sayfalandırılmış, sağdan sola bir kitap tersten okunur; bu, ana dili o dil olan herkes için hemen fark edilir ama değilseniz kolayca gözden kaçabilir.

## Sık yapılan hatalar, özetle

1. En net fotoğraf yerine en güzel fotoğrafı kullanmak. En güzel fotoğraf genellikle gün batımında arkadan aydınlatılmıştır. Bu da en kötü durumdur.
2. Her sayfaya bakmadan onaylamak.
3. Doğum gününden dört gün önce sipariş vermek.
4. Yetişkin tercih ettiği için iki yaşındaki bir çocuk için suluboya seçmek.

Bunların hiçbiri telafisi imkânsız değildir, ama hepsi önlenebilir.`,
	faqs: [
		{
			question: 'Kişiye özel bir hikâye kitabı için en iyi fotoğraf türü nedir?',
			answer:
				'Yüzün tamamının görünür ve net olduğu, eşit ve yumuşak ışıkla çekilmiş, net ve cepheden bir fotoğraf. Pencere kenarındaki iç mekân ışığı, güçlü gölgeler yaratan parlak dış mekân güneşinden genellikle daha iyi sonuç verir. Şapka, güneş gözlüğü, aşırı açılar ve ağır filtrelerden kaçının.',
		},
		{
			question: 'Kitap basılmadan önce illüstrasyonları değiştirebilir miyim?',
			answer:
				'Evet. Karakter önce oluşturulur, böylece benzerliği erken aşamada kontrol edebilirsiniz ve baskıdan önce her sayfa incelenir. Doğru olmayan sayfalar tek tek yeniden oluşturulabilir.',
		},
		{
			question: 'Kişiye özel bir hikâye kitabı ne uzunlukta olmalı?',
			answer:
				'Yaklaşık on iki resimli sayfa, sekiz yaşın altındaki çoğu çocuğa uyar — eksiksiz bir hikâye için yeterince uzun, yatmadan önce tek seferde okunacak kadar kısa.',
		},
		{
			question: 'Hangi sanat stili en iyi benzerliği verir?',
			answer:
				'Pixar tarzı 3D ve düz modern illüstrasyon genellikle ilk bakışta en tanınabilir benzerliği verir. Suluboya ve klasik hikâye kitabı stilleri ise daha yumuşak ve izlenimcidir; birçok ebeveyn bir hatıra eşyası için bunları tercih eder.',
		},
	],
	ui: {
		homeLabel: 'Ana Sayfa',
		blogLabel: 'Blog',
		faqHeading: 'Sıkça Sorulan Sorular',
		moreReadingHeading: 'Diğer Yazılar',
		ctaLabel: 'Kişiye Özel Hediyelere Göz At',
	},
};

export default post;
